import React, { useCallback, useState } from 'react';
import { X, Trash2, Plus, Loader2 } from 'lucide-react';
import PillDropdown from '../../components/ui/PillDropdown';
import apiClient from '../../services/apiClient';
import { DARK, PROTOCOL_BADGE_COLOR, btnStyle, CARD_INGEST_SPIN_KEYFRAMES } from './theme';

export default function RemoteConnectionsPanel({ c, connections, onRefresh, onClose, embedded = false }) {
  const isDarkMode = c === DARK;
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: '', protocol: 'sftp', host: '', port: '', username: '', password: '', rootPath: '/' });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const setField = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const submit = useCallback(async () => {
    if (!form.name.trim() || !form.host.trim() || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await apiClient.post('/api/ftp-connections', {
        ...form,
        port: form.port ? Number(form.port) : undefined,
      });
      setForm({ name: '', protocol: 'sftp', host: '', port: '', username: '', password: '', rootPath: '/' });
      setShowAdd(false);
      await onRefresh();
    } catch (err) {
      setSaveError(err?.response?.data?.message || err.message || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  }, [form, saving, onRefresh]);

  const remove = useCallback(async (id) => {
    await apiClient.delete(`/api/ftp-connections/${id}`);
    await onRefresh();
  }, [onRefresh]);

  const inputStyle = {
    width: '100%', padding: '6px 9px', fontSize: '12px', borderRadius: '6px',
    border: `1px solid ${c.rule}`, background: c.bg, color: c.text, fontFamily: 'inherit',
    boxSizing: 'border-box',
  };
  const labelStyle = { fontSize: '10px', color: c.muted, marginBottom: '3px', display: 'block' };

  const content = (
    <>
      <style>{CARD_INGEST_SPIN_KEYFRAMES}</style>
      {!embedded && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
          <span style={{ fontSize: '13px', fontWeight: 700 }}>Remote Server Connections</span>
          <button onClick={onClose} style={btnStyle(c, 'ghost')}><X size={12} /></button>
        </div>
      )}
        <div style={{ fontSize: '11px', color: c.muted, marginBottom: '14px', lineHeight: 1.5 }}>
          Footage copied off a card can also be pushed to any number of saved servers (FTP/FTPS/SFTP) as it offloads — same verified-copy step, just extra destinations. Passwords are encrypted at rest.
        </div>

        {connections.length === 0 && !showAdd && (
          <div style={{ fontSize: '12px', color: c.muted, marginBottom: '12px' }}>No saved connections yet.</div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' }}>
          {connections.map(conn => (
            <div key={conn.id} style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 10px',
              border: `1px solid ${c.rule}`, borderRadius: '7px',
            }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{conn.name}</div>
                <div style={{ fontSize: '10.5px', color: c.muted, fontFamily: 'monospace' }}>{conn.username ? `${conn.username}@` : ''}{conn.host}:{conn.port}{conn.rootPath}</div>
              </div>
              <span style={{
                fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
                padding: '2px 7px', borderRadius: '999px', color: c[PROTOCOL_BADGE_COLOR[conn.protocol]] || c.muted,
                border: `1px solid ${c[PROTOCOL_BADGE_COLOR[conn.protocol]] || c.rule}`,
              }}>
                {conn.protocol}
              </span>
              <button onClick={() => remove(conn.id)} style={btnStyle(c, 'ghost')} title="Delete connection">
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>

        {!showAdd && (
          <button onClick={() => setShowAdd(true)} style={btnStyle(c, 'ghost')}>
            <Plus size={13} /> Add connection
          </button>
        )}

        {showAdd && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '12px', border: `1px solid ${c.rule}`, borderRadius: '8px' }}>
            <div>
              <span style={labelStyle}>Name</span>
              <input value={form.name} onChange={e => setField('name', e.target.value)} placeholder="Production server" style={inputStyle} />
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <span style={labelStyle}>Protocol</span>
                <PillDropdown
                  value={form.protocol}
                  onChange={(v) => setField('protocol', v)}
                  isDarkMode={isDarkMode}
                  options={[
                    { value: 'sftp', label: 'SFTP' },
                    { value: 'ftps', label: 'FTPS' },
                    { value: 'ftp', label: 'FTP' },
                  ]}
                  triggerWidth="100%"
                />
              </div>
              <div style={{ flex: 2 }}>
                <span style={labelStyle}>Host</span>
                <input value={form.host} onChange={e => setField('host', e.target.value)} placeholder="ftp.example.com" style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={labelStyle}>Port</span>
                <input value={form.port} onChange={e => setField('port', e.target.value)} placeholder={form.protocol === 'sftp' ? '22' : '21'} style={inputStyle} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <span style={labelStyle}>Username</span>
                <input value={form.username} onChange={e => setField('username', e.target.value)} style={inputStyle} />
              </div>
              <div style={{ flex: 1 }}>
                <span style={labelStyle}>Password</span>
                <input type="password" value={form.password} onChange={e => setField('password', e.target.value)} style={inputStyle} />
              </div>
            </div>
            <div>
              <span style={labelStyle}>Root path</span>
              <input value={form.rootPath} onChange={e => setField('rootPath', e.target.value)} placeholder="/footage" style={inputStyle} />
            </div>
            {saveError && <div style={{ fontSize: '11px', color: c.err }}>{saveError}</div>}
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
              <button onClick={submit} disabled={saving || !form.name.trim() || !form.host.trim()} style={{ ...btnStyle(c, 'primary'), opacity: (saving || !form.name.trim() || !form.host.trim()) ? 0.5 : 1 }}>
                {saving ? <Loader2 size={12} style={{ animation: 'cardIngestSpin 1s linear infinite' }} /> : null} Save
              </button>
              <button onClick={() => setShowAdd(false)} style={btnStyle(c, 'ghost')}>Cancel</button>
            </div>
          </div>
        )}
    </>
  );

  if (embedded) return <div style={{ width: '100%' }}>{content}</div>;

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: 480, maxHeight: '80vh', overflow: 'auto', background: c.bg, borderRadius: '10px', border: `1px solid ${c.rule}`, padding: '18px 20px' }}
      >
        {content}
      </div>
    </div>
  );
}
